import * as THREE from 'three';

// StatTrak LED display constants
const DISPLAY_WIDTH = 0.04;   // Width in world units
const DISPLAY_HEIGHT = 0.012; // Height in world units
const CANVAS_WIDTH = 256;     // Canvas pixel width
const CANVAS_HEIGHT = 64;     // Canvas pixel height
const LED_COLOR = '#ff6a00';  // CS:GO StatTrak orange
const LED_BG = '#0a0a0a';    // Near-black background
const MAX_DIGITS = 7;         // Display up to 9,999,999 kills

// Per-weapon StatTrak display position offsets (hand-tuned for each weapon type)
const STATTRAK_POSITIONS: Record<string, { pos: [number, number, number]; rot: [number, number, number] }> = {
  // Rifles - on the receiver, right side
  ak47:    { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  m4a4:    { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  m4a1s:   { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  famas:   { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  galil:   { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  aug:     { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  sg553:   { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  // SMGs - similar to rifles but slightly adjusted
  mp9:     { pos: [0.024, 0.040, -0.05], rot: [0, 0, 0] },
  mac10:   { pos: [0.024, 0.040, -0.05], rot: [0, 0, 0] },
  p90:     { pos: [0.024, 0.040, -0.05], rot: [0, 0, 0] },
  // Snipers - on receiver side
  awp:     { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  ssg08:   { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  // Pistols - on slide, right side
  glock:   { pos: [0.020, 0.035, -0.08], rot: [0, 0, 0] },
  usp:     { pos: [0.020, 0.035, -0.08], rot: [0, 0, 0] },
  p250:    { pos: [0.020, 0.035, -0.08], rot: [0, 0, 0] },
  fiveseven: { pos: [0.020, 0.035, -0.08], rot: [0, 0, 0] },
  tec9:    { pos: [0.020, 0.035, -0.08], rot: [0, 0, 0] },
  deagle:  { pos: [0.022, 0.038, -0.10], rot: [0, 0, 0] },
  // Shotgun
  nova:    { pos: [0.028, 0.045, -0.06], rot: [0, 0, 0] },
  // Knife - on blade flat
  knife:   { pos: [0.006, 0.035, -0.18], rot: [0, 0, 0] },
};

/**
 * StatTrak kill counter display.
 *
 * Creates a small LED screen mesh that displays the kill count
 * as orange digits on a dark background (like CS:GO's StatTrak).
 * Attached as a child of the weapon group in the viewmodel.
 *
 * Wiring status (F-CM-007, 2026-05-10): the display class itself is
 * production-ready, but the integration paths in viewmodel.ts
 * (`attachStatTrak`, `statTrakKill`) and the kill-event broadcast that
 * should call them are not yet hooked up. The wiring lives outside the
 * cosmetics scope: viewmodel.ts must call `attachStatTrak(killCount)` on
 * weapon switch when the equipped instance carries `statTrakKills >= 0`,
 * and the kill-event handler must call `statTrakKill()` for the local
 * player. This is tracked as a viewmodel-side wiring task.
 */
export class StatTrakDisplay {
  private mesh: THREE.Mesh | null = null;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: THREE.CanvasTexture;
  private currentCount = 0;
  private targetCount = 0;
  private animating = false;
  private animTimer = 0;

  // Glow effect
  private glowMesh: THREE.Mesh | null = null;

  constructor() {
    // Create off-screen canvas for LED display
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_WIDTH;
    this.canvas.height = CANVAS_HEIGHT;
    this.ctx = this.canvas.getContext('2d')!;

    // Create texture from canvas
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;

    // Render initial state
    this.renderDigits(0);
  }

  /**
   * Create the StatTrak display mesh and attach it to a weapon group.
   *
   * @param weaponGroup - The weapon's THREE.Group to attach to
   * @param weaponId - The weapon type for positioning
   * @param killCount - Initial kill count to display
   * @returns The created mesh, or null if weapon has no StatTrak position defined
   */
  create(weaponGroup: THREE.Group, weaponId: string, killCount: number): THREE.Mesh | null {
    // Remove existing display if any
    this.remove();

    const posConfig = STATTRAK_POSITIONS[weaponId];
    if (!posConfig) return null;

    // Create display plane
    const geometry = new THREE.PlaneGeometry(DISPLAY_WIDTH, DISPLAY_HEIGHT);
    const material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: true,
      side: THREE.FrontSide,
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.set(...posConfig.pos);
    this.mesh.rotation.set(...posConfig.rot);
    this.mesh.userData.statTrak = true;
    this.mesh.renderOrder = 999; // Render on top within viewmodel

    // Add subtle glow behind the display
    const glowGeometry = new THREE.PlaneGeometry(DISPLAY_WIDTH * 1.3, DISPLAY_HEIGHT * 1.5);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6a00,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
      side: THREE.FrontSide,
    });
    this.glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
    this.glowMesh.position.set(...posConfig.pos);
    this.glowMesh.position.x -= 0.001; // Slightly behind display
    this.glowMesh.rotation.set(...posConfig.rot);
    this.glowMesh.renderOrder = 998;

    weaponGroup.add(this.glowMesh);
    weaponGroup.add(this.mesh);

    // Set initial count
    this.currentCount = killCount;
    this.targetCount = killCount;
    this.renderDigits(killCount);

    return this.mesh;
  }

  /**
   * Update the displayed kill count.
   * Triggers a brief "counting up" animation for visual feedback.
   */
  updateCount(kills: number): void {
    if (kills === this.targetCount) return;
    this.targetCount = kills;
    this.animating = true;
    this.animTimer = 0;
  }

  /**
   * Increment the kill count by 1 (convenience for kill events).
   */
  incrementKill(): void {
    this.updateCount(this.targetCount + 1);
  }

  /**
   * Update animation state. Call every frame.
   * Handles the counting-up animation when kills increase.
   */
  update(dt: number): void {
    if (!this.animating) return;

    this.animTimer += dt;

    // Quick tick-up animation: increment displayed count towards target
    if (this.currentCount < this.targetCount) {
      // Speed: ~10 digits per second, but at least 1 per 50ms
      const tickInterval = Math.max(0.05, 0.1 / Math.max(1, this.targetCount - this.currentCount));
      if (this.animTimer >= tickInterval) {
        this.currentCount++;
        this.animTimer = 0;
        this.renderDigits(this.currentCount);
      }
    } else {
      this.currentCount = this.targetCount;
      this.animating = false;
      this.renderDigits(this.currentCount);
    }

    // Pulse glow during animation
    if (this.glowMesh) {
      const mat = this.glowMesh.material as THREE.MeshBasicMaterial;
      if (this.animating) {
        mat.opacity = 0.08 + Math.sin(this.animTimer * 20) * 0.04;
      } else {
        mat.opacity = 0.08;
      }
    }
  }

  /**
   * Remove the StatTrak display from its parent weapon group.
   */
  remove(): void {
    if (this.mesh) {
      this.mesh.parent?.remove(this.mesh);
      (this.mesh.material as THREE.Material).dispose();
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    if (this.glowMesh) {
      this.glowMesh.parent?.remove(this.glowMesh);
      (this.glowMesh.material as THREE.Material).dispose();
      this.glowMesh.geometry.dispose();
      this.glowMesh = null;
    }
  }

  /**
   * Render the kill count digits onto the canvas.
   */
  private renderDigits(count: number): void {
    const ctx = this.ctx;
    const w = CANVAS_WIDTH;
    const h = CANVAS_HEIGHT;

    // Clear with dark background
    ctx.fillStyle = LED_BG;
    ctx.fillRect(0, 0, w, h);

    // Draw border (subtle dark gray)
    ctx.strokeStyle = '#222222';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, w - 2, h - 2);

    // Draw "StatTrak™" label (small, top-left)
    ctx.fillStyle = '#444444';
    ctx.font = '12px "Courier New", monospace';
    ctx.textAlign = 'left';
    ctx.fillText('StatTrak\u2122', 8, 16);

    // Draw kill count (large, orange LED style).
    // 40px Courier bold × 7 digits would overflow the 256px canvas and
    // collide with the "StatTrak™" label. Shrink the font when needed so
    // the string plus a small right margin fits within w - label gutter.
    ctx.fillStyle = LED_COLOR;
    ctx.textAlign = 'right';
    const displayStr = String(Math.min(count, 9999999)).padStart(MAX_DIGITS, '0');
    const MIN_FONT_PX = 24;
    const MAX_FONT_PX = 40;
    const rightMargin = 10;
    const leftGutter = 80; // leaves room for the "StatTrak™" label
    const availableWidth = w - leftGutter - rightMargin;
    // Start at the design size and shrink until the measured text fits.
    let fontPx = MAX_FONT_PX;
    ctx.font = `bold ${fontPx}px "Courier New", monospace`;
    while (fontPx > MIN_FONT_PX && ctx.measureText(displayStr).width > availableWidth) {
      fontPx -= 2;
      ctx.font = `bold ${fontPx}px "Courier New", monospace`;
    }
    ctx.fillText(displayStr, w - rightMargin, h - 12);

    // Scanline effect (subtle horizontal lines for LED look)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
    for (let y = 0; y < h; y += 3) {
      ctx.fillRect(0, y, w, 1);
    }

    // Update texture
    this.texture.needsUpdate = true;
  }

  /**
   * Get the current displayed kill count.
   */
  getDisplayedCount(): number {
    return this.currentCount;
  }

  /**
   * Get the target kill count (may differ during animation).
   */
  getTargetCount(): number {
    return this.targetCount;
  }

  /**
   * Dispose all resources.
   */
  dispose(): void {
    this.remove();
    this.texture.dispose();
  }
}
